import { useCallback, useEffect, useRef, useState } from "react";
import { useConsultApolloClient } from "hooks/consultRequest";
import * as api from "./apiAdapters";
import { parseConsultError } from "./errors";
import { mapListFilters } from "./mappers";

/** Gọi một adapter với Apollo client (useAppApolloClient); lỗi được chuẩn hóa qua parseConsultError. */
export function useConsultCall() {
  const client = useConsultApolloClient();
  const call = useCallback(
    async (adapter, ...args) => {
      try {
        return await adapter(client, ...args);
      } catch (error) {
        throw parseConsultError(error);
      }
    },
    [client]
  );
  return { call, client };
}

/** Tải dữ liệu theo key; phản hồi cũ (seq khác) không ghi đè dữ liệu hiện tại. */
export function useConsultQuery(adapter, args, { enabled = true, refreshKey } = {}) {
  const { call } = useConsultCall();
  const key = JSON.stringify(args);
  const [state, setState] = useState({ data: null, loading: enabled, error: null });
  const seq = useRef(0);
  const refetch = useCallback(async () => {
    const id = ++seq.current;
    if (!enabled) return null;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await call(adapter, ...JSON.parse(key));
      if (id === seq.current) setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      if (id === seq.current) setState((s) => ({ ...s, loading: false, error }));
      return null;
    }
  }, [adapter, key, enabled, call]);
  useEffect(() => {
    refetch();
    return () => {
      seq.current += 1;
    };
  }, [refetch, refreshKey]);
  const setData = useCallback((data) => setState((s) => ({ ...s, data })), []);
  return { ...state, refetch, setData };
}

export const useProjectTypeOptions = () => useConsultQuery(api.getProjectTypeOptions, []);
export const useConsultRequestDetail = (id) => useConsultQuery(api.getConsultRequest, [id]);

/**
 * Danh sách W5. Đổi tab chỉ gọi PW1; đổi filter gọi PW1 + PW2.
 * PW1 và PW2 tách effect để key của PW2 không chứa status/page.
 */
export function useConsultRequestListData() {
  const [query, setQuery] = useState({ status: "ALL", page: 1, size: 20, filters: {} });
  const filterParams = mapListFilters(query.filters);
  const list = useConsultQuery(api.listConsultRequests, [
    {
      ...filterParams,
      ...(query.status !== "ALL" ? { status: query.status } : {}),
      page: query.page,
      size: query.size,
    },
  ]);
  const counts = useConsultQuery(api.getStatusCounts, [filterParams]);
  const setStatus = useCallback((status) => setQuery((q) => ({ ...q, status, page: 1 })), []);
  const setPage = useCallback((page, size) => setQuery((q) => ({ ...q, page, size: size || q.size })), []);
  const setFilters = useCallback((filters) => setQuery((q) => ({ ...q, filters, page: 1 })), []);
  const reload = useCallback(() => Promise.all([list.refetch(), counts.refetch()]), [list.refetch, counts.refetch]); // eslint-disable-line react-hooks/exhaustive-deps
  return { query, list, counts, setStatus, setPage, setFilters, reload };
}
